import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";

const PhoneAccountContext = createContext(null);

/**
 * PhoneAccount Provider Component
 * Manages phone accounts and selected phoneId state (no localStorage)
 */
export const PhoneAccountProvider = ({ children }) => {
  const [phoneAccounts, setPhoneAccounts] = useState([]);
  const [selectedPhoneId, setSelectedPhoneId] = useState(null);
  const [loading, setLoading] = useState(false);

  // Use ref to track selectedPhoneId to avoid dependency issues
  const selectedPhoneIdRef = useRef(selectedPhoneId);
  useEffect(() => {
    selectedPhoneIdRef.current = selectedPhoneId;
  }, [selectedPhoneId]);

  /**
   * Fetch available phone accounts from API
   */
  const fetchPhoneAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("userAccessToken");
      if (!token) {
        setPhoneAccounts([]);
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/whatsapp-accounts`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await response.json();
      if (data.success && data.data && Array.isArray(data.data)) {
        const flattened = [];

        data.data.forEach((account) => {
          if (
            account.phone_numbers &&
            Array.isArray(account.phone_numbers) &&
            account.phone_numbers.length > 0
          ) {
            account.phone_numbers.forEach((phone) => {
              flattened.push({
                wabaId: account.id || account.waba_id,
                wabaName:
                  account.account_name ||
                  account.waba_name ||
                  account.business_name ||
                  "Unknown",
                phoneId: phone.phone_id,
                phoneNumber: phone.phone_number,
                phoneName: phone.phone_name || phone.verified_name || "",
              });
            });
          } else if (account.phone_id) {
            flattened.push({
              wabaId: account.waba_id || account.id,
              wabaName:
                account.waba_name ||
                account.account_name ||
                account.business_name ||
                "Unknown",
              phoneId: account.phone_id,
              phoneNumber: account.phone_number,
              phoneName: account.verified_name || account.phone_name || "",
            });
          }
        });

        setPhoneAccounts(flattened);

        // Get current selectedPhoneId from ref (always latest value)
        const currentSelectedPhoneId = selectedPhoneIdRef.current;

        // Only auto-select first phone if no phoneId is currently selected
        // This prevents resetting user's selection
        if (!currentSelectedPhoneId && flattened.length > 0) {
          setSelectedPhoneId(flattened[0].phoneId);
        } else if (currentSelectedPhoneId && flattened.length > 0) {
          // Verify selected phoneId still exists in the list
          const phoneExists = flattened.some((acc) => acc.phoneId === currentSelectedPhoneId);
          if (!phoneExists) {
            // Selected phone no longer exists, select first available
            setSelectedPhoneId(flattened[0].phoneId);
          }
          // If phone exists, don't change selectedPhoneId - preserve user's selection
        }
      } else {
        setPhoneAccounts([]);
      }
    } catch (error) {
      console.error("Error fetching phone accounts:", error);
      setPhoneAccounts([]);
    } finally {
      setLoading(false);
    }
  }, []); // No dependencies - uses ref for selectedPhoneId

  /**
   * Set selected phone ID
   * This is the only way to change phoneId - no localStorage involved
   */
  const handleSetPhoneId = useCallback((phoneId) => {
    if (phoneId && phoneId !== selectedPhoneId) {
      console.log(`📱 Phone ID changed: ${selectedPhoneId} -> ${phoneId}`);
      setSelectedPhoneId(phoneId);
    }
  }, [selectedPhoneId]);

  /**
   * Get selected phone account object
   */
  const getSelectedPhoneAccount = useCallback(() => {
    if (!selectedPhoneId) return null;
    return phoneAccounts.find((acc) => acc.phoneId === selectedPhoneId) || null;
  }, [selectedPhoneId, phoneAccounts]);

  /**
   * Fetch phone accounts on mount
   */
  useEffect(() => {
    fetchPhoneAccounts();
  }, [fetchPhoneAccounts]);

  /**
   * Listen for phone account updates from other components
   */
  useEffect(() => {
    const handlePhoneAccountsUpdated = () => {
      fetchPhoneAccounts();
    };

    window.addEventListener("phoneAccountsUpdated", handlePhoneAccountsUpdated);

    return () => {
      window.removeEventListener("phoneAccountsUpdated", handlePhoneAccountsUpdated);
    };
  }, [fetchPhoneAccounts]);

  const value = {
    // State
    phoneAccounts,
    selectedPhoneId,
    selectedPhoneAccount: getSelectedPhoneAccount(),
    loading,
    
    // Actions
    setSelectedPhoneId: handleSetPhoneId,
    fetchPhoneAccounts,
  };

  return (
    <PhoneAccountContext.Provider value={value}>
      {children}
    </PhoneAccountContext.Provider>
  );
};

/**
 * Hook to use PhoneAccount context
 */
export const usePhoneAccount = () => {
  const context = useContext(PhoneAccountContext);
  if (!context) {
    throw new Error("usePhoneAccount must be used within PhoneAccountProvider");
  }
  return context;
};

export default PhoneAccountContext;

